/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Model, { attr } from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import BulkImportSource from 'linced-app/enums/bulk-import-source';
import BulkImportStatus from 'linced-app/enums/bulk-import-status';
import type BulkImportLogEntry from 'linced-app/models/people/bulk-import-log-entry';
import type BulkImportPerson from 'linced-app/models/people/bulk-import-person';

// API_INITIATOR is the initiator of an import from an API client.
const API_INITIATOR = { fullName: 'via Hero API' };

// Proto: people.BulkImport.
export default class BulkImport extends Model {
  declare [Type]: 'people/bulk-import';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // source is the source of this bulk import.
  @attr('number', { defaultValue: 0 })
  declare source: number;

  // initiatorId is the unique identifier of the initiator of this request.
  @attr('string', { defaultValue: '' })
  declare initiatorId: string;

  // importType denotes the type of people being imported.
  @attr('number', { defaultValue: 0 })
  declare importType: number;

  // status is the current status of this Bulk Import.
  // This will be automatically set to BulkImportStatus_PROCESSING on create.
  @attr('number', { defaultValue: 0 })
  declare status: number;

  // schoolId is the ID of the target school for this bulk import.
  @attr('string', { defaultValue: '' })
  declare schoolId: string;

  // count is the number of records to be processed (server-side calculated).
  @attr('number', { defaultValue: 0 })
  declare count: number;

  // processed is the number of records from count that have been processed.
  @attr('number', { defaultValue: 0 })
  declare processed: number;

  // financeCount is the number of Finance accounts which need to be updated
  // as part of processing this Bulk Import.
  @attr('number', { defaultValue: 0 })
  declare financeCount: number;

  // financeProcessed is the number of Finance accounts which have been processed
  // as part of processing this Bulk Import.  May not equal financeCount, if
  // some accounts don't actually need updating (but we thought they did).
  @attr('number', { defaultValue: 0 })
  declare financeProcessed: number;

  // log is a list of People processed in this Bulk Import and the
  // outcome of that processing.
  @fragmentArray('people/bulk-import-log-entry')
  declare log: FragmentArray<BulkImportLogEntry>;

  // cancelled provides a flag to say that the cancellation of the associated
  // task has been requested.
  @attr('boolean', { defaultValue: false })
  declare cancelled: boolean;

  // description is an optional human-friendly description of the import.
  @attr('string', { defaultValue: '' })
  declare description: string;

  // importData is the incoming data to be imported.  Will not be persisted within
  // this record - a BulkImportData record will be created to house it.
  // The serializer remaps this to/from `data`, which is a reserved name here.
  @fragmentArray('people/bulk-import-person')
  declare importData: FragmentArray<BulkImportPerson>;

  // progress calculates the progress percentage for the main import.
  @computed('count', 'processed', 'financeCount', 'financeProcessed')
  get progress() {
    if (this.count > 0 || this.financeCount > 0) {
      const count = this.count + this.financeCount;
      const processed = this.processed + this.financeProcessed;
      return Math.round((processed / count) * 100);
    }
    return 0;
  }

  // initiator provides either the initiator (if a person) or a stub which
  // provides a fullName property for display (if an API client). It reads the
  // person from the store, thus the route loads the person first.
  get initiator() {
    if (this.source === BulkImportSource.API) {
      return API_INITIATOR;
    }
    return this.initiatorId
      ? this.store.peekRecord('person', this.initiatorId)
      : null;
  }

  // isProcessing is a quick way to check whether to show as "in progress".
  @computed('cancelled', 'status')
  get isProcessing() {
    if (this.cancelled) {
      return false;
    }
    return this.status === BulkImportStatus.PROCESSING;
  }

  // statusIcon provides the metadata to show a status icon with tooltip.
  @computed('cancelled', 'status')
  get statusIcon() {
    if (this.cancelled) {
      // Shortcut status check to give more immediate feedback when cancelled.
      return {
        icon: 'remove_circle',
        toolTip: 'Cancelled',
      };
    }
    switch (this.status) {
      case BulkImportStatus.PROCESSING:
        return {
          icon: 'rotate-right',
          spin: true,
          toolTip: 'Processing',
        };

      case BulkImportStatus.READY:
        return {
          icon: 'check_circle',
          className: 'icon-green',
          toolTip: 'Complete',
        };

      case BulkImportStatus.FAILED:
        return {
          icon: 'warning-2',
          className: 'icon-warn',
          toolTip: 'Failed',
        };

      case BulkImportStatus.CANCELLED:
        return {
          icon: 'remove_circle',
          toolTip: 'Cancelled',
        };
    }

    // Default values in case it's not a known status.
    return {
      icon: 'help',
      toolTip: 'Unknown',
    };
  }
}
